import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link as RouterLink, useParams } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Container,
    FormControl,
    FormControlLabel,
    InputLabel,
    LinearProgress,
    MenuItem,
    Paper,
    Select,
    Stack,
    Step,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    alpha,
    CircularProgress,
} from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import { getAllSclasses } from "../../../redux/sclassRelated/sclassHandle";
import { getSchoolIdForUser } from "../../../utils/userContext";
import {
    BULK_STUDENT_COLUMNS,
    applyColumnMapping,
    applyLockedClassToRows,
    buildClassIndex,
    defaultPasswordForRoll,
    downloadCsvTemplate,
    getClassDisplayName,
    mappingNeedsReview,
    parseRawUploadFile,
    suggestColumnMapping,
    validateRowsLocally,
} from "../../../utils/bulkStudentUpload";
import { isSupabaseFunctionsApi } from "../../../config/api";
import {
    getBulkImportHistory,
    importBulkStudentsChunked,
    importBulkStudentsFromFile,
    previewBulkStudentFile,
} from "../../../utils/bulkStudentApi";
import Popup from "../../../components/Popup";

const PREVIEW_LIMIT = 10;
const STEPS = ["Upload", "Map columns", "Preview", "Confirm", "Done"];

const COLUMN_LABELS = {
    rollNum: "Student ID *",
    name: "Full name *",
    gender: "Gender *",
    dateOfBirth: "Date of birth",
    sclassName: "Class *",
    guardianName: "Guardian name",
    guardianPhone: "Guardian phone (SMS)",
    guardianRelation: "Guardian relation",
    guardianEmail: "Guardian email",
    disabilityType: "Disability",
    specialNeedsNotes: "SEN notes",
};

function SummaryCard({ title, value, tone, subtitle }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, flex: 1, minWidth: 140 }}>
            <Typography variant="caption" color="text.secondary">
                {title}
            </Typography>
            <Typography variant="h5" fontWeight={800} color={tone ? `${tone}.main` : undefined}>
                {value}
            </Typography>
            {subtitle ? (
                <Typography variant="caption" color="text.secondary">
                    {subtitle}
                </Typography>
            ) : null}
        </Paper>
    );
}

export default function BulkStudentUpload() {
    const { classId: routeClassId } = useParams();
    const dispatch = useDispatch();
    const { currentUser } = useSelector((s) => s.user);
    const { sclassesList } = useSelector((s) => s.sclass);
    const schoolId = getSchoolIdForUser(currentUser);

    const fileInputRef = useRef(null);
    const [activeStep, setActiveStep] = useState(0);
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [fileName, setFileName] = useState("");
    const [rawHeaders, setRawHeaders] = useState([]);
    const [rawRows, setRawRows] = useState([]);
    const [columnMapping, setColumnMapping] = useState({});
    const [validatedRows, setValidatedRows] = useState([]);
    const [previewStats, setPreviewStats] = useState(null);
    const [validating, setValidating] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [importMode, setImportMode] = useState("partial");
    const [notifyAdmin, setNotifyAdmin] = useState(true);
    const [confirmChecked, setConfirmChecked] = useState(false);
    const [summary, setSummary] = useState(null);
    const [importHistory, setImportHistory] = useState([]);
    const [message, setMessage] = useState("");
    const [showPopup, setShowPopup] = useState(false);
    const [popupSeverity, setPopupSeverity] = useState(undefined);

    const classes = useMemo(
        () => (Array.isArray(sclassesList) ? sclassesList : []),
        [sclassesList]
    );
    const lockedClass = useMemo(() => {
        if (!routeClassId) return null;
        return (
            classes.find((c) => String(c.id || c._id) === String(routeClassId)) || null
        );
    }, [classes, routeClassId]);
    const lockedClassName = getClassDisplayName(lockedClass);
    const classIndex = useMemo(() => buildClassIndex(classes), [classes]);
    const mappingOptions = useMemo(() => ({ skipClass: Boolean(lockedClass) }), [lockedClass]);

    useEffect(() => {
        if (schoolId) dispatch(getAllSclasses(schoolId, "Sclass"));
    }, [dispatch, schoolId]);

    useEffect(() => {
        if (schoolId) {
            getBulkImportHistory(schoolId, 5)
                .then((data) => setImportHistory(data.logs || []))
                .catch(() => {});
        }
    }, [schoolId, summary]);

    const resetWizard = () => {
        setPopupSeverity(undefined);
        setActiveStep(0);
        setSelectedFile(null);
        setFileName("");
        setRawHeaders([]);
        setRawRows([]);
        setColumnMapping({});
        setValidatedRows([]);
        setPreviewStats(null);
        setSummary(null);
        setConfirmChecked(false);
        setUploadProgress(0);
    };

    const withClassContext = useCallback(
        (rows) => applyLockedClassToRows(rows, lockedClass),
        [lockedClass]
    );

    const runValidation = useCallback(
        async (parsedRows, mapping) => {
            setValidating(true);
            try {
                const scopedRows = withClassContext(parsedRows);
                const local = validateRowsLocally(scopedRows, classIndex, lockedClass);
                let merged = local;
                let stats = {
                    willCreate: local.filter((r) => r._valid).length,
                    willUpdate: 0,
                    guardianPhoneCount: local.filter((r) => String(r.guardianPhone || "").trim()).length,
                };

                if (schoolId && selectedFile) {
                    const server = await previewBulkStudentFile(schoolId, selectedFile, mapping);
                    stats = {
                        willCreate: server.willCreate ?? 0,
                        willUpdate: server.willUpdate ?? 0,
                        guardianPhoneCount: server.guardianPhoneCount ?? 0,
                        total: server.total,
                        validCount: server.validCount,
                        invalidCount: server.invalidCount,
                    };
                    merged = local.map((row) => {
                        const sr = server.results?.find((r) => r.rowIndex === row._rowIndex);
                        if (!sr) return row;
                        const combined = sr.errors?.length
                            ? [...row._errors, ...sr.errors.filter((e) => !row._errors.includes(e))]
                            : row._errors;
                        return {
                            ...row,
                            _errors: combined,
                            _valid: combined.length === 0,
                            _willUpdate: sr.willUpdate,
                            _hasGuardianPhone: sr.hasGuardianPhone,
                        };
                    });
                }

                setValidatedRows(merged);
                setPreviewStats(stats);
            } catch (err) {
                setMessage(err.response?.data?.message || err.message || "Validation failed.");
                setShowPopup(true);
            } finally {
                setValidating(false);
            }
        },
        [classIndex, lockedClass, schoolId, selectedFile, withClassContext]
    );

    const handleFile = useCallback(
        async (file) => {
            if (!file) return;
            setSummary(null);
            setUploadProgress(0);
            setConfirmChecked(false);
            try {
                const { headers, rawRows: raw } = await parseRawUploadFile(file);
                if (!raw.length) {
                    setMessage("The file has no student rows. Check the template headers and try again.");
                    setShowPopup(true);
                    return;
                }
                const suggested = suggestColumnMapping(headers);
                setSelectedFile(file);
                setFileName(file.name);
                setRawHeaders(headers);
                setRawRows(raw);
                setColumnMapping(suggested);

                if (mappingNeedsReview(suggested, mappingOptions)) {
                    setActiveStep(1);
                } else {
                    const parsed = withClassContext(applyColumnMapping(raw, suggested));
                    await runValidation(parsed, suggested);
                    setActiveStep(2);
                }
            } catch (err) {
                setMessage(err.message || "Could not read the file.");
                setShowPopup(true);
                resetWizard();
            }
        },
        [mappingOptions, runValidation, withClassContext]
    );

    const handleMappingContinue = async () => {
        if (mappingNeedsReview(columnMapping, mappingOptions)) {
            setMessage(
                lockedClass
                    ? "Map all required fields (Student ID, name, gender) before continuing."
                    : "Map all required fields (Student ID, name, gender, class) before continuing."
            );
            setShowPopup(true);
            return;
        }
        const parsed = withClassContext(applyColumnMapping(rawRows, columnMapping));
        await runValidation(parsed, columnMapping);
        setActiveStep(2);
    };

    const validRows = useMemo(() => validatedRows.filter((r) => r._valid), [validatedRows]);
    const invalidCount = validatedRows.length - validRows.length;
    const willCreate = previewStats?.willCreate ?? validRows.filter((r) => !r._willUpdate).length;
    const willUpdate = previewStats?.willUpdate ?? validRows.filter((r) => r._willUpdate).length;
    const guardianPhoneCount =
        previewStats?.guardianPhoneCount ??
        validRows.filter((r) => String(r.guardianPhone || "").trim()).length;

    const handleImport = async () => {
        if (!validRows.length || !schoolId || !selectedFile || !confirmChecked) return;
        setUploading(true);
        setUploadProgress(0);
        setSummary(null);

        try {
            const mapping = Object.keys(columnMapping).length ? columnMapping : null;
            const payloadRows = validRows.map((r) => {
                const row = {};
                for (const col of BULK_STUDENT_COLUMNS) {
                    if (r[col] !== undefined && r[col] !== null) row[col] = r[col];
                }
                return row;
            });

            const result =
                isSupabaseFunctionsApi() && payloadRows.length > 0
                    ? await importBulkStudentsChunked(schoolId, payloadRows, {
                          mode: importMode,
                          fileName: fileName || selectedFile.name || "bulk-import.csv",
                          notifyAdmin,
                          onProgress: setUploadProgress,
                      })
                    : await importBulkStudentsFromFile(schoolId, selectedFile, {
                          mode: importMode,
                          columnMapping: mapping,
                          notifyAdmin,
                          onProgress: setUploadProgress,
                      });

            setSummary({
                imported: result.successful ?? 0,
                created: result.created ?? 0,
                updated: result.updated ?? 0,
                failed: result.failed ?? 0,
                skippedInvalid: invalidCount,
                failures: result.failures || [],
                smsSent: result.smsSent,
                importLogId: result.importLogId,
            });
            const imported = result.successful ?? 0;
            const failed = result.failed ?? 0;
            const failHint =
                imported === 0 && result.failures?.length
                    ? ` ${result.failures[0].errors?.[0] || result.failures[0].message || ""}`.trim()
                    : "";
            setPopupSeverity(
                imported > 0 && failed === 0 ? "success" : imported > 0 && failed > 0 ? "warning" : "error"
            );
            setMessage(
                imported === 0 && failed > 0
                    ? `Import finished with errors.${failHint ? ` ${failHint}` : ""}`
                    : result.message || "Import complete."
            );
            setShowPopup(true);
            setActiveStep(4);
        } catch (err) {
            const data = err.response?.data;
            setSummary(
                data
                    ? {
                          imported: data.successful ?? 0,
                          created: data.created ?? 0,
                          updated: data.updated ?? 0,
                          failed: data.failed ?? 0,
                          skippedInvalid: invalidCount,
                          failures: data.failures || [],
                          rolledBack: data.rolledBack,
                      }
                    : null
            );
            const errMsg =
                data?.message ||
                err.response?.statusText ||
                err.message ||
                "Upload failed.";
            setMessage(
                err.response?.status === 503 || err.response?.status === 546
                    ? `${errMsg} Try again in a minute — imports are sent in small batches on cloud hosting.`
                    : errMsg
            );
            setShowPopup(true);
        } finally {
            setUploading(false);
        }
    };

    const previewRows = validatedRows.slice(0, PREVIEW_LIMIT);
    const skipMapStep =
        rawHeaders.length > 0 &&
        !mappingNeedsReview(suggestColumnMapping(rawHeaders), mappingOptions);

    const visibleSteps = skipMapStep
        ? STEPS.filter((s) => s !== "Map columns")
        : STEPS;
    const stepIndex = skipMapStep && activeStep >= 1 ? activeStep - 1 : activeStep;

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.04)} 0%, ${t.palette.grey[100]} 20%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="lg">
                <Popup
                    message={message}
                    setShowPopup={setShowPopup}
                    showPopup={showPopup}
                    severity={popupSeverity}
                />

                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    alignItems={{ sm: "center" }}
                    spacing={2}
                    sx={{ mb: 3 }}
                >
                    <Box>
                        <Typography variant="overline" color="text.secondary" fontWeight={600}>
                            {lockedClass ? `Class · ${lockedClassName}` : "Students · Admin only"}
                        </Typography>
                        <Typography variant="h5" fontWeight={800}>
                            {lockedClass ? `Bulk upload — ${lockedClassName}` : "Bulk student upload"}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {lockedClass
                                ? `All rows will be enrolled in ${lockedClassName}. Class column in the file is optional. `
                                : "Import learners from CSV or Excel. "}
                            Guardian phone numbers enable fee and notice SMS.
                            Default portal password: <strong>Student</strong> + student ID (4 digits) + <strong>@</strong>
                            {" "}(e.g. ID 1 → <strong>{defaultPasswordForRoll(1)}</strong>, ID 18 →{" "}
                            <strong>{defaultPasswordForRoll(18)}</strong>).
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="outlined"
                            startIcon={<DownloadOutlinedIcon />}
                            onClick={() => downloadCsvTemplate(classes, lockedClass)}
                        >
                            Download template
                        </Button>
                        {lockedClass ? (
                            <Button
                                component={RouterLink}
                                to={`/Admin/class/addstudents/${routeClassId}`}
                                variant="text"
                            >
                                Single enrolment
                            </Button>
                        ) : (
                            <Button component={RouterLink} to="/Admin/students/chooseclass" variant="text">
                                Single enrolment
                            </Button>
                        )}
                        {lockedClass ? (
                            <Button component={RouterLink} to="/Admin/classes" variant="text">
                                Back to classes
                            </Button>
                        ) : null}
                    </Stack>
                </Stack>

                {routeClassId && classes.length > 0 && !lockedClass ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Class not found.{" "}
                        <RouterLink to="/Admin/classes">Go back to classes</RouterLink> and open bulk upload
                        from the class row.
                    </Alert>
                ) : null}

                {lockedClass ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Enrolling into <strong>{lockedClassName}</strong> — every valid row is assigned to this
                        class automatically.
                    </Alert>
                ) : null}

                <Stepper activeStep={stepIndex} alternativeLabel sx={{ mb: 3 }}>
                    {visibleSteps.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>

                {importHistory.length > 0 && activeStep === 0 ? (
                    <Paper variant="outlined" sx={{ mb: 2, p: 2, borderRadius: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            <HistoryOutlinedIcon fontSize="small" color="action" />
                            <Typography variant="subtitle2" fontWeight={700}>
                                Recent imports
                            </Typography>
                        </Stack>
                        {importHistory.slice(0, 3).map((log) => (
                            <Typography key={log.id || log._id} variant="caption" display="block" color="text.secondary">
                                {new Date(log.createdAt).toLocaleString()} — {log.fileName || "import"}:{" "}
                                {log.createdCount} created, {log.updatedCount} updated
                                {log.smsSent ? " · SMS sent" : ""}
                            </Typography>
                        ))}
                    </Paper>
                ) : null}

                {/* Step 0: Upload */}
                {activeStep === 0 ? (
                    <>
                        {classes.length === 0 ? (
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Create classes first.{" "}
                                <Button component={RouterLink} to="/Admin/addclasses" size="small">
                                    Add classes
                                </Button>
                            </Alert>
                        ) : (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Required: Student ID, name, gender, class.{" "}
                                <strong>Guardian phone</strong> is recommended for SMS alerts.
                            </Alert>
                        )}

                        <Paper
                            variant="outlined"
                            onDragOver={(e) => {
                                e.preventDefault();
                                setDragOver(true);
                            }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={onDrop}
                            sx={{
                                p: 4,
                                mb: 3,
                                textAlign: "center",
                                borderRadius: 3,
                                borderStyle: "dashed",
                                borderWidth: 2,
                                borderColor: dragOver ? "primary.main" : "divider",
                                bgcolor: dragOver ? (t) => alpha(t.palette.primary.main, 0.06) : "background.paper",
                                cursor: "pointer",
                            }}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                hidden
                                accept=".csv,.xlsx,.xls"
                                onChange={(e) => handleFile(e.target.files?.[0])}
                            />
                            <CloudUploadOutlinedIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
                            <Typography variant="h6" fontWeight={700}>
                                Drag & drop your file here
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                CSV or Excel · up to 10 MB · large files processed in batches
                            </Typography>
                            {fileName ? (
                                <Chip
                                    icon={<UploadFileOutlinedIcon />}
                                    label={fileName}
                                    color="primary"
                                    variant="outlined"
                                    sx={{ mt: 2 }}
                                />
                            ) : null}
                        </Paper>
                    </>
                ) : null}

                {/* Step 1: Column mapping */}
                {activeStep === 1 ? (
                    <Paper variant="outlined" sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
                            Map your columns
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Your file headers do not match the template exactly. Match each field to a column in your
                            file. Guardian phone is used for SMS fee reminders and notices.
                        </Typography>
                        <Stack spacing={2}>
                            {BULK_STUDENT_COLUMNS.map((field) => (
                                <FormControl key={field} fullWidth size="small">
                                    <InputLabel>{COLUMN_LABELS[field] || field}</InputLabel>
                                    <Select
                                        label={COLUMN_LABELS[field] || field}
                                        value={columnMapping[field] || ""}
                                        onChange={(e) =>
                                            setColumnMapping((m) => ({ ...m, [field]: e.target.value }))
                                        }
                                    >
                                        <MenuItem value="">
                                            <em>— Not mapped —</em>
                                        </MenuItem>
                                        {rawHeaders.map((h) => (
                                            <MenuItem key={h} value={h}>
                                                {h}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ))}
                        </Stack>
                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                            <Button onClick={resetWizard}>Cancel</Button>
                            <Button variant="contained" onClick={handleMappingContinue}>
                                Continue to preview
                            </Button>
                        </Stack>
                    </Paper>
                ) : null}

                {/* Step 2: Preview */}
                {activeStep === 2 ? (
                    <>
                        {validating ? (
                            <Stack alignItems="center" py={3}>
                                <CircularProgress size={28} sx={{ mb: 1 }} />
                                <Typography variant="body2" color="text.secondary">
                                    Validating rows and checking existing student IDs…
                                </Typography>
                            </Stack>
                        ) : (
                            <>
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
                                    <SummaryCard title="Total rows" value={validatedRows.length} />
                                    <SummaryCard title="Ready" value={validRows.length} tone="success" />
                                    <SummaryCard title="New students" value={willCreate} tone="primary" />
                                    <SummaryCard title="Updates" value={willUpdate} />
                                    <SummaryCard
                                        title="Guardian phones"
                                        value={guardianPhoneCount}
                                        tone="info"
                                        subtitle="For SMS alerts"
                                    />
                                    <SummaryCard
                                        title="Need fixes"
                                        value={invalidCount}
                                        tone={invalidCount ? "error" : undefined}
                                    />
                                </Stack>

                                <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, overflow: "hidden" }}>
                                    <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: "divider" }}>
                                        <Typography variant="subtitle1" fontWeight={700}>
                                            Preview (first {Math.min(PREVIEW_LIMIT, validatedRows.length)} rows)
                                        </Typography>
                                    </Box>
                                    <TableContainer sx={{ maxHeight: 420 }}>
                                        <Table size="small" stickyHeader>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>#</TableCell>
                                                    {BULK_STUDENT_COLUMNS.map((col) => (
                                                        <TableCell key={col}>{COLUMN_LABELS[col] || col}</TableCell>
                                                    ))}
                                                    <TableCell>Status</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {previewRows.map((row) => (
                                                    <TableRow
                                                        key={row._rowIndex}
                                                        sx={{
                                                            bgcolor: row._valid
                                                                ? undefined
                                                                : (t) => alpha(t.palette.error.main, 0.08),
                                                        }}
                                                    >
                                                        <TableCell>{row._rowIndex + 1}</TableCell>
                                                        {BULK_STUDENT_COLUMNS.map((col) => (
                                                            <TableCell key={col}>
                                                                {col === "guardianPhone" && row[col] ? (
                                                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                                                        <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
                                                                        <span>{row[col]}</span>
                                                                    </Stack>
                                                                ) : col === "sclassName" && row._classLabel ? (
                                                                    `${row[col]} (${row._classLabel})`
                                                                ) : (
                                                                    row[col] || "—"
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                        <TableCell>
                                                            {row._valid ? (
                                                                <Chip
                                                                    size="small"
                                                                    color={row._willUpdate ? "warning" : "success"}
                                                                    label={row._willUpdate ? "Update" : "New"}
                                                                />
                                                            ) : (
                                                                row._errors.map((err) => (
                                                                    <Typography
                                                                        key={err}
                                                                        variant="caption"
                                                                        color="error.main"
                                                                        display="block"
                                                                    >
                                                                        {err}
                                                                    </Typography>
                                                                ))
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </Paper>

                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    <Button onClick={resetWizard}>Start over</Button>
                                    <Button
                                        variant="contained"
                                        disabled={!validRows.length}
                                        onClick={() => {
                                            setConfirmChecked(false);
                                            setActiveStep(3);
                                        }}
                                    >
                                        Review & confirm
                                    </Button>
                                </Stack>
                            </>
                        )}
                    </>
                ) : null}

                {/* Step 3: Confirm */}
                {activeStep === 3 ? (
                    <Paper variant="outlined" sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                            Confirm import
                        </Typography>
                        <Stack spacing={1.5} sx={{ mb: 3 }}>
                            <Typography variant="body1">
                                File: <strong>{fileName}</strong>
                            </Typography>
                            <Typography variant="body1">
                                <strong>{willCreate}</strong> new students · <strong>{willUpdate}</strong> updates ·{" "}
                                <strong>{invalidCount}</strong> rows skipped (validation errors)
                            </Typography>
                            <Typography variant="body1">
                                <PhoneOutlinedIcon sx={{ fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }} />
                                <strong>{guardianPhoneCount}</strong> guardian phone numbers will be saved for SMS
                                notifications
                            </Typography>
                        </Stack>

                        <FormControl fullWidth size="small" sx={{ mb: 2, maxWidth: 320 }}>
                            <InputLabel>Import mode</InputLabel>
                            <Select
                                label="Import mode"
                                value={importMode}
                                onChange={(e) => setImportMode(e.target.value)}
                            >
                                <MenuItem value="partial">
                                    Partial — import valid rows, report failures
                                </MenuItem>
                                <MenuItem value="allOrNothing">
                                    All or nothing — roll back if any row fails
                                </MenuItem>
                            </Select>
                        </FormControl>

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={notifyAdmin}
                                    onChange={(e) => setNotifyAdmin(e.target.checked)}
                                />
                            }
                            label="Send summary SMS to school admin when import completes"
                            sx={{ display: "block", mb: 2 }}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={confirmChecked}
                                    onChange={(e) => setConfirmChecked(e.target.checked)}
                                />
                            }
                            label={`I confirm importing ${validRows.length} student record${validRows.length === 1 ? "" : "s"} (${willCreate} new, ${willUpdate} updates)`}
                        />

                        {uploading ? (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Importing… {uploadProgress}%
                                </Typography>
                                <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 8, borderRadius: 4 }} />
                            </Box>
                        ) : null}

                        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                            <Button onClick={() => setActiveStep(2)} disabled={uploading}>
                                Back
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<CloudUploadOutlinedIcon />}
                                disabled={!confirmChecked || uploading || !validRows.length}
                                onClick={handleImport}
                            >
                                Import now
                            </Button>
                        </Stack>
                    </Paper>
                ) : null}

                {/* Step 4: Done */}
                {activeStep === 4 && summary ? (
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                            {summary.failed === 0 ? (
                                <CheckCircleOutlineIcon color="success" />
                            ) : (
                                <ErrorOutlineIcon color="warning" />
                            )}
                            <Typography variant="h6" fontWeight={700}>
                                Import complete
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            <strong>{summary.imported}</strong> processed ({summary.created ?? 0} created,{" "}
                            {summary.updated ?? 0} updated)
                            {summary.skippedInvalid
                                ? ` · ${summary.skippedInvalid} skipped (validation)`
                                : ""}
                            {summary.smsSent ? " · Summary SMS sent to admin" : ""}
                            {summary.rolledBack ? " · All changes rolled back" : ""}
                        </Typography>
                        {summary.failures?.length ? (
                            <Box sx={{ mt: 1, mb: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Failed rows
                                </Typography>
                                {summary.failures.slice(0, 15).map((f, i) => (
                                    <Typography key={i} variant="caption" display="block" color="error.main">
                                        Row {f.sourceRow ?? (f.rowIndex ?? 0) + 1}: {f.name || "Unknown"} —{" "}
                                        {(f.errors || []).join("; ")}
                                    </Typography>
                                ))}
                            </Box>
                        ) : null}
                        <Stack direction="row" spacing={2}>
                            <Button
                                component={RouterLink}
                                to={
                                    lockedClass && routeClassId
                                        ? `/Admin/students/class/${routeClassId}`
                                        : "/Admin/students"
                                }
                                variant="outlined"
                            >
                                {lockedClass ? `View ${lockedClassName}` : "View students"}
                            </Button>
                            <Button onClick={resetWizard}>Import another file</Button>
                        </Stack>
                    </Paper>
                ) : null}
            </Container>
        </Box>
    );
}
